import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { KeyRound, MoreVertical, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Table, type TableColumn } from '@/_shared/components/Table';
import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/_shared/components/ui/empty';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { formatDateTimeTitle, timeAgo } from '@/_shared/lib/format';

import { scopeBadgeVariant, scopeLabel, SUB_HEADING } from './constants';
import { CreateTokenDialog } from './CreateTokenDialog';
import { DeleteTokenDialog } from './DeleteTokenDialog';
import { SectionLoader } from './McpSectionPrimitives';
import type { AccessToken, NewlyCreatedAccessToken, TokenScope } from './types';

export function AccessTokensSection(): React.ReactElement {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAdmin } = useWorkspace();
  const orpc = useOrpc();

  const { data: tokenData, isLoading: tokensLoading } = useQuery(orpc.mcpAccessToken.list.queryOptions({ input: {} }));
  const tokens: AccessToken[] = tokenData?.tokens ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [tokenScope, setTokenScope] = useState<TokenScope>('mcp:read mcp:write');
  // On by default for owners and admins; the server refuses it for a member. Re-seeded from the live
  // role each time the dialog opens, so a role that resolves or changes later is honoured.
  const [manageContext, setManageContext] = useState(isAdmin);
  const [newlyCreatedToken, setNewlyCreatedToken] = useState<NewlyCreatedAccessToken | null>(null);
  const [pendingTokenRevoke, setPendingTokenRevoke] = useState<{ tokenHash: string; name: string } | null>(null);

  const createTokenMutation = useMutation(
    orpc.mcpAccessToken.create.mutationOptions({
      onSuccess: (result) => {
        setNewlyCreatedToken({ token: result.token, expiresAt: result.expiresAt });
        setTokenName('');
        void queryClient.invalidateQueries({
          queryKey: orpc.mcpAccessToken.list.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to create token'),
    }),
  );

  const revokeTokenMutation = useMutation(
    orpc.mcpAccessToken.revoke.mutationOptions({
      onSuccess: () => {
        setPendingTokenRevoke(null);
        toast.success('Access token revoked');
        void queryClient.invalidateQueries({
          queryKey: orpc.mcpAccessToken.list.queryOptions({ input: {} }).queryKey,
        });
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to revoke token'),
    }),
  );

  const handleCreateToken = useCallback(() => {
    if (!tokenName.trim()) return;
    createTokenMutation.mutate({ name: tokenName.trim(), scope: tokenScope, manageContext: isAdmin && manageContext });
  }, [createTokenMutation, isAdmin, manageContext, tokenName, tokenScope]);

  const confirmRevokeToken = useCallback(
    (tokenHash: string) => {
      revokeTokenMutation.mutate({ tokenHash });
    },
    [revokeTokenMutation],
  );

  const handleOpenCreate = useCallback(() => {
    setManageContext(isAdmin);
    setCreateOpen(true);
  }, [isAdmin]);

  const handleCloseCreate = useCallback(() => {
    setCreateOpen(false);
    setNewlyCreatedToken(null);
    setTokenName('');
  }, []);

  const tokenColumns = useMemo<TableColumn<AccessToken>[]>(
    () => [
      {
        id: 'createdBy',
        header: 'Created by',
        sortable: false,
        cell: (token) => (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar className="size-8 shrink-0">
              <AvatarImage src={token.avatarUrl ?? undefined} alt={token.displayName} />
              <AvatarFallback>{token.displayName[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{token.displayName}</p>
              {token.email ? <p className="truncate text-xs text-muted-foreground">{token.email}</p> : null}
            </div>
          </div>
        ),
      },
      {
        id: 'name',
        header: 'Token',
        sortable: false,
        width: '18rem',
        cell: (token) => (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{token.name}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className="font-mono text-[0.625rem]">
                {token.tokenPrefix}…
              </Badge>
              <Badge variant={scopeBadgeVariant(token.scope)} className="text-[0.625rem]">
                {scopeLabel(token.scope)}
              </Badge>
            </div>
          </div>
        ),
      },
      {
        id: 'activity',
        header: 'Activity',
        align: 'right',
        width: '10rem',
        sortable: false,
        cell: (token) => (
          <div className="min-w-0 text-xs text-muted-foreground">
            <p className="truncate" title={formatDateTimeTitle(token.createdAt)}>
              Created {timeAgo(token.createdAt)}
            </p>
            <p className="mt-0.5 truncate" title={token.lastUsedAt ? formatDateTimeTitle(token.lastUsedAt) : undefined}>
              {token.lastUsedAt ? `Last used ${timeAgo(token.lastUsedAt)}` : 'Never used'}
            </p>
            <p className="mt-0.5 truncate" title={formatDateTimeTitle(token.expiresAt)}>
              {!dayjs(token.expiresAt).isAfter(dayjs()) ? 'Expired' : `Expires ${timeAgo(token.expiresAt)}`}
            </p>
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        headerAriaLabel: 'Actions',
        align: 'right',
        width: '3.5rem',
        sortable: false,
        cell: (token) => {
          const canDelete = user?.id === token.userId || isAdmin;
          if (!canDelete) return null;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="tertiary" iconOnly Icon={<MoreVertical />} aria-label={`Actions for ${token.name}`} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => setPendingTokenRevoke({ tokenHash: token.tokenHash, name: token.name })}
                >
                  <Trash2 className="size-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [isAdmin, user?.id],
  );

  return (
    <>
      <div>
        <div className="mb-3 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h3 className={SUB_HEADING}>
              Access tokens
              {tokens.length > 0 && <span className="ml-1.5 text-muted-foreground/50">{tokens.length}</span>}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              For programmatic access — CI pipelines, scripts, custom MCP clients.
            </p>
          </div>
          <CreateTokenDialog
            createOpen={createOpen}
            onOpenChange={(open) => (open ? handleOpenCreate() : handleCloseCreate())}
            newlyCreatedToken={newlyCreatedToken}
            tokenName={tokenName}
            onTokenNameChange={setTokenName}
            tokenScope={tokenScope}
            onTokenScopeChange={setTokenScope}
            isAdmin={isAdmin}
            manageContext={manageContext}
            onManageContextChange={setManageContext}
            onCreateToken={handleCreateToken}
            createPending={createTokenMutation.isPending}
          />
        </div>
        {tokensLoading ? (
          <SectionLoader />
        ) : tokens.length === 0 ? (
          <Empty className="border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <KeyRound />
              </EmptyMedia>
              <EmptyTitle>No access tokens</EmptyTitle>
              <EmptyDescription>Create a token for programmatic access to your workspace MCP tools.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Table
            data={tokens}
            columns={tokenColumns}
            getRowId={(token) => token.tokenHash}
            hideHeader
            fixedLayout
            maxHeight="24rem"
          />
        )}
      </div>

      <DeleteTokenDialog
        token={pendingTokenRevoke}
        onOpenChange={(open) => {
          if (!open) setPendingTokenRevoke(null);
        }}
        isDeleting={revokeTokenMutation.isPending}
        onConfirm={confirmRevokeToken}
      />
    </>
  );
}
